import { useMemo, useCallback } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import {
  Typography,
  Button,
  Switch,
  ActivityIndicator,
  VStack,
  FlatListItemSeparator,
} from "@smartrent/ui";
import { startCase } from "lodash-es";

import { useDrawerNav } from "@/common/AppDrawer";
import {
  useCMWFeatureFlagsQuery,
  useCMWFeatureFlagMutation,
} from "@/api/cmw-feature-flags";
import { Gate } from "@/types";

import { useCMWFeatureFlagHandlers } from "../hooks";

import { EntityList } from "./EntityList";

export function CMWFeatureFlagDetails({
  flagName,
  refetch,
}: {
  flagName: string;
  refetch?: () => void;
}) {
  const drawer = useDrawerNav();
  const { data: flags, isLoading } = useCMWFeatureFlagsQuery();
  const [updateFeatureFlag] = useCMWFeatureFlagMutation();
  const { navigateToOrganization, navigateToGroup, handleRemoveGate } =
    useCMWFeatureFlagHandlers(refetch);

  const flag = useMemo(() => {
    return flags?.find((f) => f.name === flagName);
  }, [flags, flagName]);

  const { globalGate, organizations, groups } = useMemo(() => {
    if (!flag) {
      return {
        globalGate: null as Gate | null,
        organizations: [] as any[],
        groups: [] as any[],
      };
    }

    return flag.gates.reduce(
      (memo, gate) => {
        if (gate.for === "global") {
          memo.globalGate = gate;
        } else if (
          typeof gate.for === "object" &&
          gate.for.entity === "organization"
        ) {
          memo.organizations.push({
            ...gate.for,
            enabled: gate.enabled,
          });
        } else if (
          typeof gate.for === "object" &&
          gate.for.entity === "group"
        ) {
          memo.groups.push({
            ...gate.for,
            enabled: gate.enabled,
          });
        }
        return memo;
      },
      {
        globalGate: null as Gate | null,
        organizations: [] as any[],
        groups: [] as any[],
      }
    );
  }, [flag]);

  const handleGlobalToggle = useCallback(
    async (enabled: boolean) => {
      if (!flag) {
        return;
      }

      await updateFeatureFlag({
        flag: flag.name,
        payload: { enabled },
      });
      if (refetch) {
        refetch();
      }
    },
    [flag, updateFeatureFlag, refetch]
  );

  const handleEntityToggle = useCallback(
    async (
      entityType: "organization" | "group",
      entityId: number,
      enabled: boolean
    ) => {
      if (!flag) {
        return;
      }

      const payload =
        entityType === "organization"
          ? { enabled, organization_id: entityId }
          : { enabled, group_id: entityId };

      await updateFeatureFlag({
        flag: flag.name,
        payload,
      });
      if (refetch) {
        refetch();
      }
    },
    [flag, updateFeatureFlag, refetch]
  );

  const handleAddOrganization = useCallback(() => {
    if (!flag) {
      return;
    }

    drawer.push("addFeatureFlagGate", {
      flagName: flag.name,
      refetch,
      entity: "organization",
    });
  }, [drawer, flag, refetch]);

  const handleAddGroup = useCallback(() => {
    if (!flag) {
      return;
    }

    drawer.push("addFeatureFlagGate", {
      flagName: flag.name,
      refetch,
      entity: "group",
    });
  }, [drawer, flag, refetch]);

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size={24} />
      </View>
    );
  }

  if (!flag) {
    return (
      <View style={styles.container}>
        <Typography type="body">Flag not found</Typography>
      </View>
    );
  }

  return (
    <ScrollView>
      <View style={styles.container}>
        <Typography type="title3">{startCase(flag.name)}</Typography>

        <View style={styles.section}>
          <Switch
            name="globalEnabled"
            label="Enabled Globally"
            onValueChange={handleGlobalToggle}
            value={globalGate?.enabled || false}
          />
        </View>

        <FlatListItemSeparator />

        <EntityList
          entities={organizations}
          title="Organizations"
          entityType="organization"
          onEntityClick={navigateToOrganization}
          onRemoveEntity={handleRemoveGate}
          onEntityToggle={handleEntityToggle}
          flagName={flag.name}
        />
        <EntityList
          entities={groups}
          title="Properties"
          entityType="group"
          onEntityClick={navigateToGroup}
          onRemoveEntity={handleRemoveGate}
          onEntityToggle={handleEntityToggle}
          flagName={flag.name}
        />

        <VStack spacing={8} style={styles.section}>
          <Button onPress={handleAddOrganization} style={styles.button}>
            Add Organization
          </Button>
          <Button onPress={handleAddGroup} style={styles.button}>
            Add Group
          </Button>
        </VStack>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
  loadingContainer: {
    padding: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  section: {
    marginVertical: 8,
  },
  button: {
    marginBottom: 8,
  },
});
